/**
 * nj-typed-address-verifier: the event designer's typed address (CC 0418, 0419, 0421).
 *
 * Since CC 0421 the address is only CHECKED on blur, for feedback; the location is created and linked at
 * Save, from the values on screen at that moment. So no answer that arrives while the author keeps editing
 * can write to the record or the held change.
 *
 * - createAddressFeedback: the blur check. Typing (or picking a saved location, switching mode or opening
 *   another event) invalidates any check in flight; checks of the same trimmed text share one run.
 * - createTypedLocationLinker: the Save step. One location per trimmed address AND trimmed venue name for
 *   this designer session (CC 0433): the same pair reuses its location, and two Saves of the same pair share
 *   one create in flight. The same address under another venue name makes its own location, so one event's
 *   name is never handed to another.
 */
import type { TypedLocationResult } from "./nj-typed-location";

export type VerifyState = "idle" | "checking" | "invalid" | "valid";
export interface FeedbackDeps {
  check: (text: string) => Promise<{ valid: boolean }>;
  onState: (state: VerifyState) => void;
}

export function createAddressFeedback(deps: () => FeedbackDeps) {
  let gen = 0;
  const pending = new Map<string, Promise<{ valid: boolean }>>();
  return {
    invalidate() { gen += 1; },
    verify(text: string) {
      const q = text.trim();
      if (q.length < 4) { gen += 1; deps().onState("idle"); return; }
      const run = ++gen;
      deps().onState("checking");
      let p = pending.get(q);
      if (!p) { p = deps().check(q).catch(() => ({ valid: false })).finally(() => { pending.delete(q); }); pending.set(q, p); }
      void p.then((r) => { if (run === gen) deps().onState(r.valid ? "valid" : "invalid"); });
    },
  };
}

export function createTypedLocationLinker() {
  const saved = new Map<string, Extract<TypedLocationResult, { kind: "saved" }>>();
  const pending = new Map<string, Promise<TypedLocationResult>>();
  return {
    /** The location for this address and venue name: an existing one, the create already in flight, or a
     *  new create. A failed or invalid answer is not remembered, so the next Save tries again. */
    ensure(text: string, venueName: string, create: (text: string, venueName: string) => Promise<TypedLocationResult>): Promise<TypedLocationResult> {
      const q = text.trim();
      const v = (venueName ?? "").trim();
      const key = JSON.stringify([q, v]);
      const known = saved.get(key);
      if (known) return Promise.resolve(known);
      let p = pending.get(key);
      if (!p) {
        p = create(q, v).then((r) => { if (r.kind === "saved") saved.set(key, r); return r; }).finally(() => { pending.delete(key); });
        pending.set(key, p);
      }
      return p;
    },
  };
}
